import * as Sentry from '@sentry/nextjs'
import { RcFile } from 'antd/lib/upload'

const MAX_RETRIES = 3
const RETRY_DELAY = 300

export const uploadWithProgress = async (
  url: string,
  file: RcFile | File,
  onProgress?: (e: ProgressEvent) => void,
  allowRetry = false
) => {
  const upload = (): Promise<XMLHttpRequest> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const startTime = Date.now()

      xhr.addEventListener('load', () => resolve(xhr), false)
      xhr.addEventListener(
        'error',
        () => {
          const connection = navigator['connection']

          Sentry.captureMessage('Upload failed', {
            extra: {
              // XHR State
              status: xhr.status,
              readyState: xhr.readyState,
              responseText: xhr.responseText?.slice(0, 1000), // Limit size
              statusText: xhr.statusText,

              // Network State
              online: navigator.onLine,
              connectionType: connection?.effectiveType,
              downlink: connection?.downlink,
              rtt: connection?.rtt,

              // File Info
              fileSize: file.size,
              fileType: file.type,
              fileName: file.name,

              // Request Context
              uploadDuration: Date.now() - startTime,
              userAgent: navigator.userAgent,
              attempt: attempt + 1,
              maxAttempts: retriesAllowed,
            },
          })
          reject(new Error('File upload failed'))
        },
        false
      )
      xhr.addEventListener(
        'abort',
        () => reject(new Error('File upload aborted')),
        false
      )

      if (onProgress) {
        xhr.upload.addEventListener('progress', onProgress, false)
      }

      const properFile = new File([file], file.name, { type: file.type })
      xhr.open('PUT', url, true)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(properFile)
    })
  }

  let attempt = 0
  const retriesAllowed = allowRetry ? MAX_RETRIES : 1
  while (attempt < retriesAllowed) {
    try {
      const xhr = await upload()
      if (xhr.status !== 0) {
        return xhr
      }
      console.warn(
        `Upload attempt ${attempt + 1} returned status 0, retrying...`
      )
    } catch (error) {
      console.warn(`Upload attempt ${attempt + 1} failed:`, error)
      Sentry.captureException(error)
      if (attempt === retriesAllowed - 1) throw error
    }

    attempt++
    if (attempt < retriesAllowed) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
    }
  }

  throw new Error('Upload failed after retries')
}
